import React from 'react';
import { Camera, LayoutDashboard, FilePlus, Clock, BookOpen, Scissors, Calendar, Settings, LogOut, ChevronRight, ClipboardList, GitBranch, LayoutGrid, Film, IndianRupee, CheckSquare } from 'lucide-react';
import { User } from '../types';

type Page = 'dashboard' | 'new-quote' | 'quote-history' | 'walk-in-album' | 'walk-in-pp' | 'calendar' | 'settings' | 'general-planner' | 'enquiry-tracker' | 'booking-tracker' | 'master-enquiry' | 'cost-management' | 'pp-tracker' | 'audit-checklist' | 'lifecycle-map' | 'lesson-learned';

interface SidebarProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
  user: User;
  onLogout: () => void;
  onShowGuide: () => void;
  onSwitchMode: () => void;
}

const NAV_ITEMS: { id: Page; label: string; icon: React.ReactNode; adminOnly?: boolean }[] = [
  { id: 'dashboard',     label: 'Dashboard',         icon: <LayoutDashboard size={15} /> },
  { id: 'new-quote',     label: 'New Quote',          icon: <FilePlus size={15} /> },
  { id: 'quote-history', label: 'Quote History',      icon: <Clock size={15} /> },
  { id: 'walk-in-album', label: 'Walk-in Album',      icon: <BookOpen size={15} /> },
  { id: 'walk-in-pp',    label: 'Walk-in Post Prod.', icon: <Scissors size={15} /> },
  { id: 'calendar',      label: 'Booking Calendar',   icon: <Calendar size={15} /> },
  { id: 'general-planner', label: 'General Planner', icon: <Camera size={15} /> },
  { id: 'settings',      label: 'Settings',           icon: <Settings size={15} />, adminOnly: true },
];

// Lifecycle phases — numbered and grouped
const LIFECYCLE_PHASES = [
  {
    phase: 1, label: 'Enquiry',
    items: [
      { id: 'lifecycle-map',    label: 'Lifecycle Map',          icon: <LayoutGrid size={15} /> },
      { id: 'master-enquiry',   label: 'Master Enquiry View',    icon: <LayoutGrid size={15} /> },
      { id: 'enquiry-tracker',  label: 'Enquiry Tracker',        icon: <ClipboardList size={15} /> },
    ]
  },
  {
    phase: 2, label: 'Booking & Tracking',
    items: [
      { id: 'booking-tracker',  label: 'Booking & Stage Tracker', icon: <GitBranch size={15} /> },
    ]
  },
  {
    phase: 3, label: 'Execution',
    items: [
      { id: 'audit-checklist',  label: 'Event Audit Checklist',  icon: <CheckSquare size={15} /> },
    ]
  },
  {
    phase: 4, label: 'Post-Production',
    items: [
      { id: 'pp-tracker',       label: 'Post-Production Tracker', icon: <Film size={15} /> },
    ]
  },
  {
    phase: 5, label: 'Financials',
    items: [
      { id: 'cost-management',  label: 'Cost Management',        icon: <IndianRupee size={15} /> },
    ]
  },
  {
    phase: 6, label: 'Review & Improve',
    items: [
      { id: 'lesson-learned',   label: 'Lesson Learned',         icon: <BookOpen size={15} /> },
    ]
  },
];
const LIFECYCLE_ITEMS: { id: Page; label: string; icon: React.ReactNode }[] = LIFECYCLE_PHASES.flatMap(p => p.items as any);

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin', coo: 'COO', md: 'MD', cto: 'CTO', employee: 'Employee'
};

export default function Sidebar({ currentPage, onNavigate, user, onLogout, onShowGuide, onSwitchMode }: SidebarProps) {
  return (
    <div style={{
      width: 220, height: '100vh',
      background: '#1A202C',
      borderRight: 'none',
      display: 'flex', flexDirection: 'column',
      position: 'fixed', top: 0, left: 0, zIndex: 100,
    }}>
      {/* Logo */}
      <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, background: 'rgba(183,121,31,0.2)', border: '1px solid rgba(183,121,31,0.4)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Camera size={17} color="#D69E2E" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#F7FAFC' }}>Shivam Studio</div>
            <div style={{ fontSize: 10, color: '#718096' }}>Rate Planner 2.0</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
        {NAV_ITEMS.map(item => {
          if (item.adminOnly && user.role !== 'admin') return null;
          const active = currentPage === item.id;
          return (
            <button key={item.id} onClick={() => onNavigate(item.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '9px 12px', borderRadius: 6, marginBottom: 1,
                background: active ? 'rgba(183,121,31,0.15)' : 'transparent',
                border: active ? '1px solid rgba(183,121,31,0.3)' : '1px solid transparent',
                color: active ? '#D69E2E' : '#A0AEC0',
                cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
                transition: 'all 0.12s', textAlign: 'left',
              }}
              onMouseEnter={e => { if (!active) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.color = '#F7FAFC'; }}}
              onMouseLeave={e => { if (!active) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#A0AEC0'; }}}
            >
              <span>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {active && <ChevronRight size={11} style={{ opacity: 0.5 }} />}
            </button>
          );
        })}

        {/* Lifecycle Management section */}
        <div style={{ margin: '14px 12px 6px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#4A5568' }}>
          Lifecycle Management
        </div>
        {LIFECYCLE_PHASES.map(phase => (
          <div key={phase.phase} style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#4B5563', letterSpacing: '0.8px', textTransform: 'uppercase',
              padding: '6px 12px 3px', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ background: 'var(--gold)', color: '#fff', borderRadius: 100, width: 14, height: 14,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800, flexShrink: 0 }}>
                {phase.phase}
              </span>
              {phase.label}
            </div>
            {phase.items.map((item: any) => {
          const active = currentPage === item.id;
          return (
            <button key={item.id} onClick={() => onNavigate(item.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '9px 12px', borderRadius: 6, marginBottom: 1,
                background: active ? 'rgba(183,121,31,0.15)' : 'transparent',
                border: active ? '1px solid rgba(183,121,31,0.3)' : '1px solid transparent',
                color: active ? '#D69E2E' : '#A0AEC0',
                cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
                transition: 'all 0.12s', textAlign: 'left',
              }}
              onMouseEnter={e => { if (!active) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.color = '#F7FAFC'; }}}
              onMouseLeave={e => { if (!active) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#A0AEC0'; }}}
            >
              <span>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {active && <ChevronRight size={11} style={{ opacity: 0.5 }} />}
            </button>
          );
            })}
          </div>
        ))}
      </nav>

      {/* User */}
      <div style={{ padding: '10px 8px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ padding: '8px 12px', marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#F7FAFC' }}>{user.name}</div>
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 100, background: 'rgba(183,121,31,0.2)', color: '#D69E2E', border: '1px solid rgba(183,121,31,0.3)', fontWeight: 600 }}>
            {ROLE_LABELS[user.role]}
          </span>
        </div>
        <button onClick={onShowGuide}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 6, background: 'transparent', border: '1px solid transparent', color: '#718096', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>?</span> Navigation Guide
        </button>
        <button onClick={onLogout}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, background: 'transparent', border: '1px solid transparent', color: '#718096', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', transition: 'all 0.12s' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(197,48,48,0.12)'; (e.currentTarget as HTMLElement).style.color = '#FC8181'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#718096'; }}
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </div>
  );
}
